import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SkeletonLoader from "../../components/SkeletonLoader";
import RfiGridTable from "../../components/RFIs/RfiGridTable";
import Pagination from "../../components/CustomPagination";
import Notification from "../../components/Notification";
import {
  useGetMyRfis,
  useGetMyRfisAllPages,
  useGetprovidersListForFilters,
  useListOfQuestionnaireTemplate,
} from "../../custom-hooks/queries";
import { useTemplateFilterOptionsFromMyRfis } from "../../custom-hooks/useCatalogFilterOptions";
import { mapQuestionnaireTemplateToFilterOption } from "../../utils/catalogFilterOptions";
import { useDispatch, useSelector } from "react-redux";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import PageToolbarFilters from "../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../components/Filters/ToolbarSearch";
import TableExportMenu from "../../components/Menus/TableExportMenu";
import { rfiCellStyles } from "../../utils/exportBadgeStyles";
import {
  mapMyRfiItemToGridRow,
  mapGridRowToExportRecord,
  MY_RFI_EXPORT_HEADERS,
  MY_RFIS_USE_SERVER_SORT,
  sortMyRfiItems,
  sortConfigToApiSortParams,
  filterMyRfiItemsByClientFilters,
} from "../../utils/myRfiGridRowMapper";

const MY_RFI_TYPE_FILTER_OPTIONS = [
  { value: "diligent", label: "Diligent VQQ" },
  { value: "custom", label: "Custom VQQ" },
];

export const MyRFIs = () => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    requestDate: { direction: "desc" },
  });
  const [sortFetchNonce, setSortFetchNonce] = useState(0);

  const { data: providers } = useGetprovidersListForFilters();

  const { data: questionnares } = useListOfQuestionnaireTemplate();

  const { data: allRfisForTemplates, isFetched: myRfisAllPagesFetched } =
    useGetMyRfisAllPages("", [], [], [], []);

  const templatesFilterOptions = useTemplateFilterOptionsFromMyRfis(
    questionnares,
    allRfisForTemplates?.items,
    myRfisAllPagesFetched,
    mapQuestionnaireTemplateToFilterOption
  );

  const [selectedIds, setSelectedIds] = useState({
    auditor: [],
    provider: [],
    status: [],
    templates: [],
    services: [],
    type: [],
    gxp: [],
  });
  const [dueDateFilter, setDueDateFilter] = useState({ startDate: "", endDate: "" });

  const searchQuery =
    useSelector((state) => state.providersFilters.searchTextByPage["myRfis"]) ||
    "";

  const filters = useMemo(
    () => [
      {
        id: "provider",
        name: "Vendor",
        searchable: true,
        options: providers || [],
        total: 1335,
      },
      {
        id: "type",
        name: "Type",
        searchable: false,
        options: MY_RFI_TYPE_FILTER_OPTIONS,
        total: MY_RFI_TYPE_FILTER_OPTIONS.length,
      },
      // TODO: uncomment for enable GXP filter (import MY_RFI_GXP_FILTER_OPTIONS from myRfiGridRowMapper)
      // {
      //   id: "gxp",
      //   name: "GxP",
      //   searchable: true,
      //   options: MY_RFI_GXP_FILTER_OPTIONS,
      //   total: MY_RFI_GXP_FILTER_OPTIONS.length,
      // },
      {
        id: "templates",
        name: "Templates",
        searchable: true,
        options: templatesFilterOptions,
        total: templatesFilterOptions?.length || 0,
      },
      {
        id: "status",
        name: "Status",
        searchable: false,
        options: [
          { statusID: "Sent", label: "Sent", checked: false },
          { statusID: "In Progress", label: "In Progress", checked: false },
          { statusID: "In Review", label: "In Review", checked: false },
          {
            statusID: "In Remediation (Vendor)",
            label: "In Remediation (Vendor)",
            checked: false,
          },
          {
            statusID: "In Remediation (Purchaser)",
            label: "In Remediation (Purchaser)",
            checked: false,
          },
          { statusID: "Complete", label: "Complete", checked: false },
          { statusID: "Cancelled", label: "Cancelled", checked: false },
        ],
        total: 7,
      },
      {
        id: "dueDate",
        name: "Due Date",
        type: "date",
        value: dueDateFilter,
        onChange: setDueDateFilter,
      },
    ],
    [providers, templatesFilterOptions, dueDateFilter]
  );

  const serverSortParams = useMemo(() => {
    if (!MY_RFIS_USE_SERVER_SORT) return undefined;
    return sortConfigToApiSortParams(sortConfig);
  }, [sortConfig]);

  const {
    data: rfisData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetMyRfis(
    currentPage,
    21,
    searchQuery,
    [],
    selectedIds.templates,
    selectedIds.status,
    selectedIds.services,
    selectedIds.provider,
    serverSortParams,
    sortFetchNonce,
    dueDateFilter
  );

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const newDirection =
        prevConfig[key]?.direction === "asc" ? "desc" : "asc";
      return { [key]: { direction: newDirection } };
    });
    if (MY_RFIS_USE_SERVER_SORT) {
      setSortFetchNonce((n) => n + 1);
      setCurrentPage(1);
    }
  };

  const sortedItems = useMemo(() => {
    const items = rfisData?.items || [];
    const afterLocal = filterMyRfiItemsByClientFilters(items, selectedIds);
    if (MY_RFIS_USE_SERVER_SORT) {
      return afterLocal;
    }
    return sortMyRfiItems(afterLocal, sortConfig);
  }, [rfisData?.items, selectedIds, sortConfig]);

  const navigate = useNavigate();

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedIds, dueDateFilter]);

  const exportDataRef = useRef(null);
  const handleExportDataReady = useCallback((fn) => {
    exportDataRef.current = fn;
  }, []);

  useEffect(() => {
    const getData = () => {
      const records = sortedItems.map((item) =>
        mapGridRowToExportRecord(mapMyRfiItemToGridRow(item))
      );
      return { records, headers: [...MY_RFI_EXPORT_HEADERS] };
    };
    handleExportDataReady(getData);
  }, [sortedItems, handleExportDataReady]);

  const handleRowClick = useCallback(
    (item, event) => {
      if (event.target.closest("button") || event.target.closest("a")) return;
      if (!item?.v1 && item?.questionnaire_id) {
        navigate(`/RFI/${item.questionnaire_id}/Details`);
      }
    },
    [navigate]
  );

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">My VQQs</h1>
            <p className="mt-1 text-sm text-gray-500">
              The below table displays all active and historical Vendor Qualification Questionnaire (VQQ) requests.
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle="VQQ Requests"
              reportSubtitle="All active and historical VQQ requests"
              filePrefix="VQQ_Requests"
              downloadType="VQQ Requests"
              cellStyles={rfiCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({ pageKey: "myRfis", searchText: text })
                )
              }
              placeholder="Search your VQQ request details..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
          <RfiGridTable
            data={sortedItems}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={handleRowClick}
            refetch={refetch}
            isSearchActive={!!searchQuery}
          />
        )}

        {!isError &&
          !isLoading &&
          sortedItems &&
          sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                totalPages={rfisData?.total_pages || 1}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
      </div>

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error && error.message}
        status="error"
      />
    </div>
  );
};
